import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { HttpApiService } from '@recruitee/http';
import {
  CandidateReference,
  OfferBriefReference,
  Placement,
  Position,
  StageReference,
} from '@recruitee/http-types';

export type PlacementHttpReference = CandidateReference | OfferBriefReference | StageReference;
export interface PlacementChangeStageParams {
  id: number;
  stageId: number | string;
  runActions?: boolean | null;
  proceed?: boolean;
  hiredAt?: string;
  jobStartsAt?: string;
  openingId?: number | null;
  workLocationId?: number | null;
  disqualifyReasonId?: number;
  disqualifyRunActions?: boolean;
  removeFromSharingContainers?: boolean;
}
export interface PlacementDisqualifyParams {
  id: number;
  disqualifyReasonId: number | string;
  runActions?: boolean | null;
}
export interface UpdateHiringDetailsParams {
  placementId: number;
  hiredAt: string;
  jobStartsAt: string;
  openingId: number | null;
  workLocationId: number | null;
}

@Injectable({ providedIn: 'root' })
export class PlacementHttpClient {
  constructor(protected httpApi: HttpApiService) {}

  public create({
    offerId,
    candidateId,
    locationIds,
    talentPoolId,
  }: {
    offerId?: number;
    talentPoolId?: number;
    candidateId: number;
    locationIds?: number[] | null;
  }): Observable<{ placement: Placement; references: PlacementHttpReference[] }> {
    return this.httpApi.post(`/placements`, { offerId, talentPoolId, candidateId, locationIds });
  }

  public delete({
    id,
  }: {
    id: number;
  }): Observable<{ placement: Placement; references: PlacementHttpReference[] }> {
    return this.httpApi.delete(`/placements/${id}`);
  }

  public updatePositions(
    positions: Position[],
  ): Observable<{ placements: Placement[]; references: PlacementHttpReference[] }> {
    return this.httpApi.patch(`/placements/update_positions`, null, {
      placements: positions,
    });
  }

  public updateCandidatePositions(
    positions: Position[],
  ): Observable<{ placements: Placement[]; references: PlacementHttpReference[] }> {
    return this.httpApi.patch(`/placements/update_candidate_positions`, null, {
      placements: positions,
    });
  }

  public requalify(
    id: number,
  ): Observable<{ placement: Placement; references: PlacementHttpReference[] }> {
    return this.httpApi.patch(`/placements/${id}/requalify`);
  }

  public disqualify({
    id,
    disqualifyReasonId,
    runActions = null,
  }: PlacementDisqualifyParams): Observable<{
    placement: Placement;
    references: PlacementHttpReference[];
  }> {
    return this.httpApi.patch(`/placements/${id}/disqualify`, {
      disqualifyReasonId,
      ...(runActions !== null && { runActions }),
    });
  }

  public updatePreferredLocations(
    placementId: number,
    locationIds: number[],
  ): Observable<{ placement: Placement; references: PlacementHttpReference[] }> {
    return this.httpApi.patch(`/placements/${placementId}/locations`, null, {
      placement: { locationIds },
    });
  }

  public changeStage({
    id,
    stageId,
    runActions = null,
    proceed = false,
    hiredAt = '',
    jobStartsAt = '',
    openingId = null,
    workLocationId = null,
    disqualifyReasonId,
    disqualifyRunActions,
    removeFromSharingContainers,
  }: PlacementChangeStageParams): Observable<{
    placement: Placement;
    references: PlacementHttpReference[];
  }> {
    return this.httpApi.patch(`/placements/${id}/change_stage`, {
      stage_id: stageId,
      ...(runActions !== null && { run_actions: runActions }),
      proceed,
      hiredAt,
      jobStartsAt,
      openingId,
      workLocationId,
      disqualifyReasonId,
      disqualifyRunActions,
      removeFromSharingContainers,
    });
  }

  public fetchHiringDetails(placementId: number): Observable<{
    placement: Placement;
    references: PlacementHttpReference[];
  }> {
    return this.httpApi.get(`/placements/${placementId}/hiring_details`);
  }

  public updateHiringDetails({
    placementId,
    hiredAt,
    jobStartsAt,
    openingId,
    workLocationId,
  }: UpdateHiringDetailsParams): Observable<{
    placement: Placement;
    references: PlacementHttpReference[];
  }> {
    return this.httpApi.patch(`/placements/${placementId}/hiring_details`, null, {
      placement: {
        hiredAt,
        ...(jobStartsAt?.length ? { jobStartsAt } : {}),
        openingId,
        workLocationId,
      },
    });
  }
}
